import React, { useCallback, useMemo, useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Alert, Box, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useGetTranslationOverviewCatalogs } from '~/hooks/apis/customer-communication/useTranslationOverview';
import useDebounced from '~/hooks/useDebounced';

import dateFormatter from '~/utils/dateFormatter';

import { TRANSLATION_CATALOG_BRANDS } from '../TranslationCatalog/translationCatalogUtils';

import CatalogLocalesDrawer from './CatalogLocalesDrawer';
import CoverageCell from './CoverageCell';
import SelectionActionBar from './SelectionActionBar';
import TranslationOverviewToolbar from './TranslationOverviewToolbar';
import UploadStatusCell from './UploadStatusCell';
import {
  MUTATE_BLOCKED_REASON,
  SEARCH_DEBOUNCE_MS,
  TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE,
  TRANSLATION_OVERVIEW_PAGE_SIZES,
  canUploadCatalog,
  countUploadableRows,
  formatPairCount,
} from './translationOverviewUtils';
import type {
  TranslationOverviewCatalogRow,
  TranslationOverviewStatus,
  TranslationOverviewUploadFilter,
} from './types';

const UPLOAD_BLOCKED_REASON = 'Nothing waiting — every translated locale of this catalog is already in Braze';

interface Props {
  onTranslate: (catalogIds: Array<string>, pairCount: number) => void;
  onRefresh: (catalogIds: Array<string>, pairCount: number) => void;
  onUpload: (catalogIds: Array<string>, rowCount: number) => void;
  canMutate: boolean;
}

function getColumns({
  onOpenMenu,
}: {
  onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: TranslationOverviewCatalogRow) => void;
}): Array<GridColDef<TranslationOverviewCatalogRow>> {
  return [
    {
      field: 'catalogName',
      headerName: 'Catalog',
      flex: 1.4,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {row.catalogName}
        </Typography>
      ),
    },
    {
      field: 'brand',
      headerName: 'Brand',
      width: 140,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: 'readyCount',
      headerName: 'Locale coverage',
      flex: 1.6,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <CoverageCell counts={row} total={row.totalLocales} />,
    },
    {
      field: 'failedCount',
      headerName: 'Failed',
      width: 110,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" color={row.failedCount ? 'error.main' : 'text.secondary'} fontWeight={600}>
          {row.failedCount.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'pendingUploadCount',
      headerName: 'Braze',
      width: 150,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <UploadStatusCell counts={row} isUploadable={canUploadCatalog(row)} blockedReason={UPLOAD_BLOCKED_REASON} />
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 170,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <>{dateFormatter(row.updatedAt)}</>,
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      align: 'right',
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          aria-label={`Actions for ${row.catalogName}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenMenu(event, row);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
}

export default function CatalogsTab({ onTranslate, onRefresh, onUpload, canMutate }: Props) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'catalogName', sort: 'asc' }]);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<TranslationOverviewStatus | ''>('');
  const [uploadStatus, setUploadStatus] = useState<TranslationOverviewUploadFilter | ''>('');
  const [brand, setBrand] = useState('');
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [menuRow, setMenuRow] = useState<TranslationOverviewCatalogRow | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [openCatalog, setOpenCatalog] = useState<TranslationOverviewCatalogRow | null>(null);

  const search = useDebounced(searchInput, SEARCH_DEBOUNCE_MS);

  const listParams = useMemo(
    () => ({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      search: search.trim() || undefined,
      status: status || undefined,
      uploadStatus: uploadStatus || undefined,
      brand: brand || undefined,
      sortBy: sortModel[0]?.field,
      sortDirection: sortModel[0]?.sort ?? undefined,
    }),
    [paginationModel, search, status, uploadStatus, brand, sortModel],
  );

  const { data, isFetching, error } = useGetTranslationOverviewCatalogs(listParams);
  const rows = data?.rows ?? [];
  const rowCount = data?.total ?? 0;

  const columns = useMemo(
    () =>
      getColumns({
        onOpenMenu: (event, row) => {
          setMenuAnchor(event.currentTarget);
          setMenuRow(row);
        },
      }),
    [],
  );

  // A selected catalog stands for every locale of that catalog, so the pair maths uses the widest row.
  const localesPerCatalog = rows.length ? Math.max(...rows.map((row) => row.totalLocales)) : 0;
  const selectedPairCount = selectedIds.length * localesPerCatalog;

  /**
   * How many rows the selection would send, counted from the page currently rendered.
   *
   * Only an estimate on purpose: `keepNonExistentRowsSelected` lets a selection span pages, and the
   * rows for other pages are not in memory. The whole selection is sent regardless — the API skips
   * locales that are not Ready or have nothing waiting, and its 202 reports how many were really
   * queued — so this number only has to be good enough for the confirmation copy.
   */
  const visibleUploadRowCount = useMemo(
    () =>
      rows
        .filter((row) => selectedIds.some((id) => String(id) === row.catalogId))
        .filter(canUploadCatalog)
        .reduce((total, row) => total + countUploadableRows(row), 0),
    [rows, selectedIds],
  );

  const resetToFirstPage = useCallback(() => {
    setPaginationModel((model) => ({ ...model, page: 0 }));
    setSelectedIds([]);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuRow(null);
  }, []);

  const handleMenuAction = useCallback(
    (action: 'translate' | 'refresh' | 'upload' | 'view') => {
      if (!menuRow) {
        return;
      }

      if (action === 'view') {
        setOpenCatalog(menuRow);
      } else if (action === 'translate') {
        onTranslate([menuRow.catalogId], menuRow.totalLocales);
      } else if (action === 'refresh') {
        onRefresh([menuRow.catalogId], menuRow.totalLocales);
      } else {
        onUpload([menuRow.catalogId], countUploadableRows(menuRow));
      }

      handleCloseMenu();
    },
    [menuRow, onTranslate, onRefresh, onUpload, handleCloseMenu],
  );

  return (
    <Stack spacing={2}>
      <TranslationOverviewToolbar
        search={searchInput}
        onSearchChange={(value) => {
          setSearchInput(value);
          resetToFirstPage();
        }}
        searchPlaceholder="Search catalogs"
        status={status}
        onStatusChange={(value) => {
          setStatus(value);
          resetToFirstPage();
        }}
        uploadStatus={uploadStatus}
        onUploadStatusChange={(value) => {
          setUploadStatus(value);
          resetToFirstPage();
        }}
        brand={brand}
        brandOptions={TRANSLATION_CATALOG_BRANDS}
        onBrandChange={(value) => {
          setBrand(value);
          resetToFirstPage();
        }}
      />

      <SelectionActionBar
        selectedCount={selectedIds.length}
        itemNoun="catalog"
        summary={`Acts on roughly ${formatPairCount(selectedPairCount)} across every locale of the selected catalogs.`}
        actions={[
          {
            label: 'Translate missing',
            onClick: () => onTranslate(selectedIds.map(String), localesPerCatalog),
            disabled: !canMutate,
            disabledReason: MUTATE_BLOCKED_REASON,
          },
          {
            label: 'Refresh',
            onClick: () => onRefresh(selectedIds.map(String), localesPerCatalog),
            disabled: !canMutate,
            disabledReason: MUTATE_BLOCKED_REASON,
          },
          {
            label: 'Upload to Braze',
            onClick: () => onUpload(selectedIds.map(String), visibleUploadRowCount),
            disabled: !canMutate,
            disabledReason: MUTATE_BLOCKED_REASON,
          },
        ]}
        onClear={() => setSelectedIds([])}
      />

      {!!error && <Alert severity="error">{error.message}</Alert>}

      <Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.catalogId}
          loading={isFetching}
          rowCount={rowCount}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            resetToFirstPage();
          }}
          pageSizeOptions={TRANSLATION_OVERVIEW_PAGE_SIZES}
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={setSelectedIds}
          disableRowSelectionOnClick
          onRowClick={({ row }) => setOpenCatalog(row)}
          slots={{ pagination: GridPagination }}
          autoHeight
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleMenuAction('view')}>View locales</MenuItem>
        <MenuItem disabled={!canMutate} onClick={() => handleMenuAction('translate')}>
          Translate missing
        </MenuItem>
        <MenuItem disabled={!canMutate} onClick={() => handleMenuAction('refresh')}>
          Refresh translations
        </MenuItem>
        <MenuItem
          disabled={!canMutate || !menuRow || !canUploadCatalog(menuRow)}
          onClick={() => handleMenuAction('upload')}
        >
          Upload to Braze
        </MenuItem>
      </Menu>

      <CatalogLocalesDrawer catalog={openCatalog} onClose={() => setOpenCatalog(null)} />
    </Stack>
  );
}
